/*
  Lazy looping videos. Used by sections/cb-video-band.liquid and
  sections/cb-steps.liquid.

  Markup: <video data-cb-lazy-video data-sources='[{"w":480,"u":"..."},...]' muted loop playsinline
  preload="none" poster="..."></video>. Nothing is downloaded until the video is about to scroll
  into view; then the smallest MP4 that covers its on-screen width (at up to 2x density) loads,
  plays muted while at least a quarter of it is visible, and pauses when it leaves.
  Shoppers who ask for reduced motion or to save data keep the poster only. The poster image itself
  also waits (data-cb-src / data-cb-srcset) until it is within 400 px of the screen.
  Loaded as a module, so several sections can include it and it still runs once.
*/
const SELECTOR = 'video[data-cb-lazy-video]';
const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = navigator.connection && navigator.connection.saveData;

function pickSource(video) {
  let list = [];
  try {
    list = JSON.parse(video.getAttribute('data-sources') || '[]');
  } catch (e) {
    return null;
  }
  if (!list.length) return null;
  list.sort((a, b) => a.w - b.w);
  const need = (video.getBoundingClientRect().width || 480) * Math.min(window.devicePixelRatio || 1, 1.5);
  return (list.find((s) => s.w >= need) || list[list.length - 1]).u;
}

function load(video) {
  if (video.dataset.cbLoaded) return;
  const src = pickSource(video);
  if (!src) return;
  video.dataset.cbLoaded = '1';
  video.muted = true;
  video.src = src;
  video.load();
}

function play(video) {
  const p = video.play();
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

const nearIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            nearIO.unobserve(entry.target);
            load(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      )
    : null;

const viewIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.intersectionRatio >= 0.25 && !document.hidden) {
              load(video);
              play(video);
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.25] }
      )
    : null;

function scan(root) {
  if (reduce || saveData || !nearIO) return;
  (root || document).querySelectorAll(SELECTOR).forEach((video) => {
    if (video.dataset.cbWatched) return;
    video.dataset.cbWatched = '1';
    nearIO.observe(video);
    viewIO.observe(video);
  });
}

// Posters: src and srcset wait in data attributes until the poster is near the screen. This runs for
// everyone (reduced motion and save-data visitors see only the poster).
const POSTER = 'img[data-cb-lazy-poster]';
function showPoster(img) {
  if (img.dataset.cbSrcset) img.srcset = img.dataset.cbSrcset;
  if (img.dataset.cbSrc) img.src = img.dataset.cbSrc;
  img.removeAttribute('data-cb-lazy-poster');
}
const posterIO =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            posterIO.unobserve(entry.target);
            showPoster(entry.target);
          });
        },
        { rootMargin: '400px 0px' }
      )
    : null;
function scanPosters(root) {
  (root || document).querySelectorAll(POSTER).forEach((img) => {
    if (posterIO) posterIO.observe(img);
    else showPoster(img);
  });
}

scanPosters(document);
scan(document);
document.addEventListener('shopify:section:load', (event) => {
  scanPosters(event.target);
  scan(event.target);
});
document.addEventListener('visibilitychange', () => {
  document.querySelectorAll(SELECTOR).forEach((video) => {
    if (document.hidden) {
      if (!video.paused) video.pause();
      return;
    }
    if (!video.dataset.cbLoaded) return;
    const r = video.getBoundingClientRect();
    const shown = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    if (r.height && shown / r.height >= 0.25) play(video);
  });
});
